import {
  computeDownscalePlan,
  type DownscalePlan,
  type PhotoMissingReason,
  type PhotoPolicy,
  type PunchPhotoStatus,
} from '@worklogix/shared';

/**
 * Pure, browser-free capture-decision helpers for photo-on-punch (plan-v7).
 *
 * The DOM/`getUserMedia`/`canvas` work lives at the edge (see `useCamera.ts`);
 * everything that decides an OUTCOME — which {@link PhotoMissingReason} a failure
 * maps to, whether to warn the operator, and what downscale plan a given video
 * frame implies — is factored here so it is unit-testable with no browser.
 *
 * INVARIANT (Decision 3): NOTHING here can block a punch. Capture failure is a
 * first-class `missing` outcome that is queued alongside the (already durable)
 * punch; `required` policy only escalates to a non-blocking warning.
 */

/**
 * The result of attempting a single capture at PIN submit. A `stored` outcome
 * carries the encoded JPEG blob; a `missing` outcome carries the mapped reason
 * and a null blob. `capturedAt` is the client capture time (ISO-8601).
 */
export interface CaptureOutcome {
  status: PunchPhotoStatus;
  /** Encoded JPEG when `status === 'stored'`; null when `missing`. */
  blob: Blob | null;
  /** Present only when `status === 'missing'`. */
  missingReason?: PhotoMissingReason;
  capturedAt: string;
}

/** A capturer grabs one frame from the warm camera stream. Never rejects. */
export type PhotoCapturer = () => Promise<CaptureOutcome>;

/**
 * Map a capture failure to a non-PII {@link PhotoMissingReason}. Pure: it inspects
 * only the error's `name`/shape, never touches the DOM, and is total — any
 * unrecognized error collapses to `capture_failed` so a punch is never lost to an
 * unclassified throw.
 *
 * - permission denied / blocked  → `permission_denied`
 * - no camera present / usable   → `no_camera`
 * - anything else (incl. a null `toBlob`, decode error, timeout) → `capture_failed`
 */
export function mapCaptureErrorToReason(error: unknown): PhotoMissingReason {
  const name = errorName(error);
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'permission_denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'no_camera';
    default:
      return 'capture_failed';
  }
}

/** Extract a DOMException-style `name`, tolerating strings and plain objects. */
function errorName(error: unknown): string | null {
  if (typeof error === 'string') {
    return error;
  }
  if (error !== null && typeof error === 'object' && 'name' in error) {
    const { name } = error as { name: unknown };
    return typeof name === 'string' ? name : null;
  }
  return null;
}

/**
 * Whether to show the non-blocking "photo required — please face the camera"
 * warning: only when the tenant policy is `required` AND the capture came back
 * `missing`. A stored photo (or an `optional` tenant) never warns. This NEVER
 * blocks the punch — it only drives a conspicuous UI hint.
 */
export function shouldWarnRequiredPhoto(policy: PhotoPolicy, status: PunchPhotoStatus): boolean {
  return policy === 'required' && status === 'missing';
}

/**
 * The downscale plan for a captured video frame of the given pixel dimensions —
 * a thin, testable wrapper over the shared `computeDownscalePlan` (default cap
 * 1280 px longest edge, q≈0.7, no upscale). Kept separate so the selection wiring
 * is verifiable without a real `<video>`.
 */
export function resolveCapturePlan(frameWidth: number, frameHeight: number): DownscalePlan {
  return computeDownscalePlan(frameWidth, frameHeight);
}
